import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  ArrowCounterClockwise,
  ArrowRight,
  CheckCircle,
  CreditCard,
  EnvelopeSimple,
  FileText,
  Money,
  Prohibit,
  Star,
  Users,
  XCircle,
  type Icon,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatDateTimeWithOptionsBs } from "@/lib/format";

export type AdminActionItemId =
  | "pending_cancellations"
  | "pending_refunds"
  | "undelivered_emails"
  | "pending_therapists"
  | "pending_bank_transfers"
  | "pending_reviews"
  | "pending_blog_posts"
  | "pending_payout_requests"
  | "pending_payouts"
  | "repeat_cancelling_clients";

export interface AdminActionItem {
  id: AdminActionItemId;
  priority: "P1" | "P2" | "P3";
  count: number;
  amount?: number;
  oldestAt?: string | null;
}

interface ActionConfig {
  href: string;
  search?: Record<string, string>;
  icon: Icon;
}

const ACTION_CONFIG: Record<AdminActionItemId, ActionConfig> = {
  pending_cancellations: { href: "/dashboard/admin/cancellation-requests", icon: XCircle },
  pending_refunds: { href: "/dashboard/admin/refunds", icon: ArrowCounterClockwise },
  undelivered_emails: {
    href: "/dashboard/admin/email-logs",
    search: { status: "undelivered" },
    icon: EnvelopeSimple,
  },
  pending_therapists: { href: "/dashboard/admin/therapists", icon: Users },
  pending_bank_transfers: { href: "/dashboard/admin/subscriptions", icon: CreditCard },
  pending_reviews: { href: "/dashboard/admin/reviews", icon: Star },
  pending_blog_posts: { href: "/dashboard/admin/blog", icon: FileText },
  pending_payout_requests: { href: "/dashboard/admin/payouts", icon: Money },
  pending_payouts: { href: "/dashboard/admin/payouts", icon: Money },
  repeat_cancelling_clients: { href: "/dashboard/admin/clients", icon: Prohibit },
};

type Tone = "rose" | "brand" | "neutral";

const PRIORITY_TONE: Record<AdminActionItem["priority"], Tone> = {
  P1: "rose",
  P2: "brand",
  P3: "neutral",
};

const PILL_STYLE: Record<Tone, string> = {
  rose: "bg-rose-50 text-rose-700 ring-1 ring-rose-200",
  brand: "bg-primary/10 text-primary ring-1 ring-primary/15",
  neutral: "bg-muted text-muted-foreground ring-1 ring-border/60",
};

const COUNT_STYLE: Record<Tone, string> = {
  rose: "bg-rose-600 text-white",
  brand: "bg-primary text-primary-foreground",
  neutral: "bg-muted-foreground/15 text-foreground",
};

const TAG_STYLE: Record<AdminActionItem["priority"], string> = {
  P1: "bg-rose-600 text-white",
  P2: "bg-primary/10 text-primary",
  P3: "bg-muted text-muted-foreground",
};

const RAIL_STYLE: Record<AdminActionItem["priority"], string> = {
  P1: "bg-rose-600",
  P2: "bg-primary",
  P3: "bg-muted-foreground/30",
};

function shouldShowItem(item: AdminActionItem): boolean {
  return item.count > 0 || (item.amount ?? 0) > 0;
}

function formatOldestAt(value: string | null | undefined): string | null {
  if (!value) return null;
  return formatDateTimeWithOptionsBs(value, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Sarajevo",
  });
}

export function AdminActionInbox({
  items,
  formatCurrency,
}: {
  items: AdminActionItem[];
  formatCurrency: (value: number) => string;
}) {
  const { t } = useTranslation();
  const visibleItems = items.filter(shouldShowItem);
  const totalCount = visibleItems.reduce((sum, item) => sum + item.count, 0);

  return (
    <section className="overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm">
      <header className="flex items-center justify-between gap-3 border-b border-border/60 px-5 py-4">
        <div>
          <h2 className="text-base font-semibold text-foreground" style={{ fontFamily: "var(--font-heading)" }}>
            {t("admin.dashboard.actionCenter")}
          </h2>
          <p className="text-sm text-muted-foreground">{t("admin.dashboard.actionCenterSubtitle")}</p>
        </div>
        {totalCount > 0 && (
          <span className="inline-flex min-w-7 items-center justify-center rounded-full bg-rose-600 px-2 py-1 text-xs font-semibold text-white tabular-nums">
            {totalCount}
          </span>
        )}
      </header>

      {visibleItems.length === 0 ? (
        <div className="flex flex-col items-center gap-2 px-5 py-10 text-center">
          <CheckCircle className="size-7 text-emerald-600" weight="duotone" />
          <p className="text-sm font-medium text-foreground">{t("admin.dashboard.actionCenterEmpty")}</p>
          <p className="text-sm text-muted-foreground">{t("admin.dashboard.actionCenterEmptySub")}</p>
        </div>
      ) : (
        <div className="divide-y divide-border/40">
          {visibleItems.map((item) => {
            const config = ACTION_CONFIG[item.id];
            const ItemIcon = config.icon;
            const tone = PRIORITY_TONE[item.priority];
            const oldestAt = formatOldestAt(item.oldestAt);
            const amount = item.amount ?? 0;

            return (
              <div
                key={item.id}
                className={cn(
                  "relative grid items-center gap-4 px-5 py-4 transition-colors hover:bg-muted/40",
                  "grid-cols-[auto_1fr] sm:grid-cols-[auto_1fr_auto_auto]",
                )}
              >
                <span aria-hidden className={cn("absolute inset-y-0 left-0 w-[3px]", RAIL_STYLE[item.priority])} />

                <span
                  className={cn(
                    "inline-flex size-11 shrink-0 items-center justify-center rounded-xl",
                    PILL_STYLE[tone],
                  )}
                >
                  <ItemIcon className="size-5" weight="duotone" />
                </span>

                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-semibold text-foreground">
                      {t(`admin.dashboard.actions.${item.id}.title`)}
                    </p>
                    <span
                      className={cn(
                        "rounded px-1.5 py-px text-[10px] font-bold uppercase tracking-wide",
                        TAG_STYLE[item.priority],
                      )}
                    >
                      {item.priority}
                    </span>
                    {item.count > 0 && (
                      <span
                        className={cn(
                          "inline-flex min-w-6 items-center justify-center rounded-full px-1.5 text-xs font-bold tabular-nums",
                          COUNT_STYLE[tone],
                        )}
                      >
                        {item.count}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t(`admin.dashboard.actions.${item.id}.description`)}
                    {oldestAt ? (
                      <>
                        {" · "}
                        <span className={cn(item.priority === "P1" && "font-medium text-rose-600")}>
                          {t("admin.dashboard.oldestAt", { date: oldestAt })}
                        </span>
                      </>
                    ) : null}
                  </p>
                </div>

                {amount > 0 ? (
                  <p
                    className="hidden whitespace-nowrap text-right text-base font-semibold tabular-nums text-foreground sm:block"
                    style={{ fontFamily: "var(--font-heading)" }}
                  >
                    {formatCurrency(amount)}
                  </p>
                ) : (
                  <span className="hidden sm:block" />
                )}

                <Button
                  asChild
                  size="sm"
                  variant="ghost"
                  className={cn(
                    "group/cta col-span-2 w-full justify-start font-medium sm:col-span-1 sm:w-auto sm:justify-center",
                    item.priority === "P1"
                      ? "text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                      : "text-primary hover:bg-primary/10 hover:text-primary",
                  )}
                >
                  <Link to={config.href} search={config.search}>
                    {t(`admin.dashboard.actions.${item.id}.cta`)}
                    <ArrowRight
                      data-icon="inline-end"
                      className="transition-transform group-hover/cta:translate-x-0.5"
                    />
                  </Link>
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
